import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DEFAULT_STEP = 300;

const initUsageScroll = (lenis, tabs) => {
  const section = document.querySelector('[data-usage-scroll]');

  if (!section || !tabs) {
    return;
  }

  const pin = section.querySelector('[data-usage-pin]');
  const parent = section.querySelector('[data-tabs="parent"]');
  const controls = parent ? [...parent.querySelectorAll('[data-tabs="control"]')] : [];
  const tabsCount = controls.length;

  if (!pin || !tabsCount) {
    return;
  }

  const desktopMedia = window.matchMedia('(min-width: 1200px)');
  const step = Number.parseInt(section.dataset.usageStep, 10) || DEFAULT_STEP;
  let lockedIndex = null;
  let unlockTimer = null;
  let scrollId = 0;

  const getProgressByIndex = (index) => {
    return (index + 0.5) / tabsCount;
  };

  const getIndexByProgress = (progress) => {
    if (progress >= 1) {
      return tabsCount - 1;
    }

    return Math.min(tabsCount - 1, Math.floor(progress * tabsCount));
  };

  const setSubProgress = (activeIndex, sub) => {
    controls.forEach((btn, i) => {
      btn.style.setProperty('--tab-progress', i === activeIndex ? sub : 0);
    });
  };

  const openUsageTab = (index) => {
    const control = controls[index];

    if (!control) {
      return;
    }

    tabs.openTab(control, true);
  };

  const syncToProgress = (progress) => {
    const index = getIndexByProgress(progress);
    const raw = progress * tabsCount;
    const sub = Math.min(1, raw - index);

    openUsageTab(index);
    setSubProgress(index, sub);
  };

  const unlock = () => {
    lockedIndex = null;
    window.clearTimeout(unlockTimer);
    unlockTimer = null;
  };

  const lockToIndex = (index) => {
    lockedIndex = index;
    window.clearTimeout(unlockTimer);
    setSubProgress(index, 1);
  };

  const cancelLockedScroll = () => {
    if (lockedIndex === null) {
      return;
    }

    scrollId += 1;
    unlock();

    const st = ScrollTrigger.getById('usage');

    if (st) {
      syncToProgress(st.progress);
    }
  };

  const killScroll = () => {
    const existing = ScrollTrigger.getById('usage');

    if (existing) {
      existing.kill(true);
    }

    unlock();
    setSubProgress(0, 0);
  };

  const createScroll = () => {
    killScroll();

    if (!lenis || !desktopMedia.matches) {
      return;
    }

    ScrollTrigger.create({
      id: 'usage',
      trigger: section,
      pin,
      pinSpacing: true,
      start: 'center center',
      end: `+=${tabsCount * step}`,
      fastScrollEnd: true,
      preventOverlaps: true,
      snap: {
        snapTo: (value) => {
          const index = lockedIndex !== null ? lockedIndex : getIndexByProgress(value);
          return getProgressByIndex(index);
        },
        duration: {min: 0.2, max: 0.5},
        ease: 'power1.inOut',
      },
      onUpdate(self) {
        if (lockedIndex !== null) {
          return;
        }

        syncToProgress(self.progress);
      },
    });
  };

  const onMediaChange = () => {
    createScroll();
    ScrollTrigger.refresh();
  };

  const scrollToIndex = (index) => {
    const st = ScrollTrigger.getById('usage');

    if (!st) {
      openUsageTab(index);
      setSubProgress(index, 1);
      return;
    }

    openUsageTab(index);
    lockToIndex(index);
    scrollId += 1;
    const currentScroll = scrollId;

    const targetY = st.start + getProgressByIndex(index) * (st.end - st.start);

    const onComplete = () => {
      if (currentScroll !== scrollId) {
        return;
      }

      unlock();
      openUsageTab(index);
      setSubProgress(index, 1);
    };

    unlockTimer = window.setTimeout(() => {
      if (currentScroll === scrollId) {
        cancelLockedScroll();
      }
    }, 1000);

    if (lenis) {
      lenis.scrollTo(targetY, {
        duration: 0.6,
        onComplete,
      });
      return;
    }

    window.scrollTo({top: targetY, behavior: 'smooth'});
  };

  controls.forEach((btn, index) => {
    btn.addEventListener('click', () => {
      if (!lenis || !desktopMedia.matches) {
        cancelLockedScroll();
        openUsageTab(index);
        setSubProgress(index, 0);
        return;
      }

      scrollToIndex(index);
    });
  });

  if (!lenis) {
    killScroll();
    return;
  }

  window.addEventListener('wheel', cancelLockedScroll, {passive: true});
  window.addEventListener('touchstart', cancelLockedScroll, {passive: true});
  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
  }, {once: true});

  createScroll();

  if (desktopMedia.addEventListener) {
    desktopMedia.addEventListener('change', onMediaChange);
  } else {
    desktopMedia.addListener(onMediaChange);
  }
};

export {initUsageScroll};
